import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Splits a list into pages.
 *   const pg = usePaged(list, 12);   →  pg.items (this page) · <Pagination {...pg.props}/>
 * Goes back to page 1 whenever `resetKey` changes (e.g. a filter or search changed).
 * Changing the page scrolls back to the top so the new page starts from the beginning.
 */
export function usePaged(list, perPage = 12, { resetKey, scrollTop = true, scrollTarget } = {}) {
  const all = list || [];
  const [page, setPage] = React.useState(1);
  const pages = Math.max(1, Math.ceil(all.length / perPage));
  React.useEffect(() => { setPage(1); }, [resetKey]);
  React.useEffect(() => { if (page > pages) setPage(pages); }, [page, pages]);
  const cur = Math.min(page, pages);
  const onChange = (p) => {
    setPage(p);
    if (typeof window === "undefined") return;
    const el = scrollTarget && document.getElementById(scrollTarget);
    if (el) el.scrollIntoView({ block: "start" });           // e.g. back to the start of the reviews list
    else if (scrollTop) window.scrollTo({ top: 0, behavior: "instant" });
  };
  return {
    items: all.slice((cur - 1) * perPage, cur * perPage),
    page: cur,
    setPage,
    props: { page: cur, pages, total: all.length, perPage, onChange },
  };
}

function pageList(page, pages) {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  const out = [1];
  const from = Math.max(2, page - 1);
  const to = Math.min(pages - 1, page + 1);
  if (from > 2) out.push("…a");
  for (let p = from; p <= to; p++) out.push(p);
  if (to < pages - 1) out.push("…b");
  out.push(pages);
  return out;
}

export function Pagination({ page, pages, total, perPage, onChange, label = "items", className = "" }) {
  if (!total || pages <= 1) return null;
  const from = (page - 1) * perPage + 1;
  const to = Math.min(total, page * perPage);
  const btn = "min-w-[38px] h-[38px] px-2 rounded-full text-sm flex items-center justify-center transition-colors";
  return (
    <nav className={`mt-8 flex flex-col sm:flex-row items-center justify-between gap-3 ${className}`} aria-label="Pagination" data-testid="pagination">
      <div className="text-xs text-neutral-500">Showing {from}–{to} of {total} {label}</div>
      <div className="flex items-center gap-1">
        <button type="button" onClick={() => onChange(page - 1)} disabled={page <= 1} aria-label="Previous page"
                className={`${btn} border border-neutral-300 hover:border-[#0E4D34] disabled:opacity-40 disabled:hover:border-neutral-300`} data-testid="page-prev">
          <ChevronLeft size={16}/>
        </button>
        {pageList(page, pages).map((p) => typeof p === "string"
          ? <span key={p} className="px-1 text-neutral-400">…</span>
          : <button type="button" key={p} onClick={() => onChange(p)} aria-current={p === page ? "page" : undefined}
                    className={`${btn} ${p === page ? "bg-[#0E4D34] text-white" : "hover:bg-neutral-100 text-neutral-700"}`} data-testid={`page-${p}`}>{p}</button>)}
        <button type="button" onClick={() => onChange(page + 1)} disabled={page >= pages} aria-label="Next page"
                className={`${btn} border border-neutral-300 hover:border-[#0E4D34] disabled:opacity-40 disabled:hover:border-neutral-300`} data-testid="page-next">
          <ChevronRight size={16}/>
        </button>
      </div>
    </nav>
  );
}
